import React from 'react';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import { tokens } from '../../theme/tokens';

/** Mirrors `StagedBundleComponent` in src/types/stagedProduct.ts. */
export type StagedBundleComponentRow = {
  sku: string;
  name: string;
  quantity: number;
  unitPrice?: number;
};

export interface StagedBundleComponentsProps {
  resolution?: 'resolved' | 'needs_review';
  reason?: string | null;
  optionName?: string | null;
  contentsText?: string | null;
  components?: StagedBundleComponentRow[];
  componentTotal?: number | null;
  compareAtPrice?: number | null;
}

const money = (n?: number | null) => (typeof n === 'number' ? `$${n.toFixed(2)}` : null);

/**
 * A web bundle's real-SKU components on the admin staged product detail,
 * written by the Peak Design scraper under flag `peak_design_bundle_components`.
 * Read-only: the scraper reads them from the parent product page, and a
 * needs-review bundle says why.
 */
export default function StagedBundleComponents({
  resolution,
  reason,
  optionName,
  contentsText,
  components = [],
  componentTotal,
  compareAtPrice,
}: StagedBundleComponentsProps) {
  const total = money(componentTotal);
  const target = money(compareAtPrice);
  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={resolution === 'resolved' ? 'Resolved' : 'Needs review'}
          color={resolution === 'resolved' ? 'success' : 'warning'}
          variant="outlined"
        />
        {optionName && (
          <Typography variant="body2" fontWeight={600}>
            {optionName}
          </Typography>
        )}
        {contentsText && (
          <Typography variant="body2" color="text.secondary">
            {contentsText}
          </Typography>
        )}
      </Stack>

      {resolution !== 'resolved' && (
        <Alert severity="warning" variant="outlined">
          {reason || 'The components could not be confirmed.'}
        </Alert>
      )}

      {components.length > 0 ? (
        <Box component="ul" sx={{ pl: 2, m: 0 }}>
          {components.map((c) => (
            <li key={c.sku}>
              <Typography variant="body2">
                <Box component="span" sx={{ fontFamily: tokens.font.mono }}>
                  {c.sku}
                </Box>
                {` ${c.name}`}
                {c.quantity > 1 ? ` (x${c.quantity})` : ''}
                {typeof c.unitPrice === 'number' ? (
                  <Box component="span" sx={{ color: 'text.secondary' }}>{` · ${money(c.unitPrice)}`}</Box>
                ) : null}
              </Typography>
            </li>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No component matched a catalog SKU yet.
        </Typography>
      )}

      {(total || target) && (
        <Typography variant="caption" color="text.secondary">
          {total ? `Components ${total}` : 'Components not priced'}
          {target ? ` · bundle compare-at ${target}` : ''}
        </Typography>
      )}
    </Stack>
  );
}
